<template>
  <div class="paper-analysis-container" @click.stop>
    <!-- 加载状态提示：数据未加载时显示 -->
    <div v-if="!isDataLoaded" class="loading-box">
      <div>
        <i class="el-icon-close" style="cursor: pointer; font-size: 20px; float: right;" @click="clossPaperAnalysis()"></i>
      </div>
      <div>
        <el-empty description="论文分析中，请稍后..."></el-empty>
      </div>
    </div>

    <!-- 数据加载完成后渲染 -->
    <div v-else class="box-card" shadow="hover" style="background-color: var(--bg-back); padding: 20px;">
      <el-card style="border-radius: 20px;">
        <div slot="header" class="clearfix" style="display: flex; flex-direction: row; justify-content: space-between;">
          <span style="color: var(--txt-color);">论文分析结果 --- {{ full_txt_art_analysis_data.file_title || full_txt_art_analysis_data?.title }}</span>
          <i class="el-icon-close" style="cursor: pointer; font-size: 20px;" @click="clossPaperAnalysis()"></i>
        </div>

        <!-- 基础信息：Element UI 的 el-collapse 保持原有逻辑 -->
        <el-collapse v-model="activeNames" class="custom-collapse">
          <el-collapse-item title="基础信息" name="1">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="论文标题">{{ full_txt_art_analysis_data?.title || '未收录' }}</el-descriptions-item>
              <el-descriptions-item label="出版年份">
                {{ full_txt_art_analysis_data?.pub_year || '未收录' }}
              </el-descriptions-item>
              <el-descriptions-item label="DOI编号">
                {{ cleanDoi(full_txt_art_analysis_data?.doi) || '未收录' }}
              </el-descriptions-item>
              <el-descriptions-item label="出版商">
                {{ full_txt_art_analysis_data?.publisher || '未收录' }}
              </el-descriptions-item>
              <el-descriptions-item label="语言">
                {{ full_txt_art_analysis_data?.language?.join(',') || '未收录' }}
              </el-descriptions-item>
              <el-descriptions-item label="期刊">
                {{ full_txt_art_analysis_data?.journal || '未收录' }}
              </el-descriptions-item>
              <el-descriptions-item label="开放获取">
                {{ full_txt_art_analysis_data?.is_oa ? '是' : full_txt_art_analysis_data?.is_oa === false ? '否' : '未收录' }}
              </el-descriptions-item>
              <el-descriptions-item label="成本级别">
              <!-- 自定义 class 方案：完全抛弃 Element 的 type，用自定义类 -->
              <span 
                class="cost-level-tag" 
                :class="getCostLevelClass(full_txt_art_analysis_data?.research_cost?.成本级别)"
                v-if="full_txt_art_analysis_data?.research_cost?.成本级别"
              >
                {{ full_txt_art_analysis_data?.research_cost?.成本级别 }}
              </span>
              <span v-else class="empty-tip">未收录</span>
            </el-descriptions-item>
            </el-descriptions>
          </el-collapse-item>

          <!-- 研究核心信息 -->
          <el-collapse-item title="研究核心信息" name="2">
            <el-tabs v-model="activeTab">
              <el-tab-pane label="研究目标" name="goal">
                <div class="content-box">{{ full_txt_art_analysis_data?.research_goal || '无相关内容' }}</div>
              </el-tab-pane>
              <el-tab-pane label="研究结果" name="results">
                <div class="content-box">{{ full_txt_art_analysis_data?.results || '无相关内容' }}</div>
              </el-tab-pane>
              <el-tab-pane label="研究意义" name="significance">
                <div class="content-box">{{ full_txt_art_analysis_data?.significance || '无相关内容' }}</div>
              </el-tab-pane>
              <el-tab-pane label="研究背景" name="background">
                <div class="content-box">{{ full_txt_art_analysis_data?.background || '无相关内容' }}</div>
              </el-tab-pane>
            </el-tabs>
          </el-collapse-item>

          <!-- 研究方法与步骤 -->
          <el-collapse-item title="研究方法与步骤" name="3">
            <el-descriptions :column="1" border>
              <el-descriptions-item label="研究方法">
                {{ full_txt_art_analysis_data?.research_methods || '无相关内容' }}
              </el-descriptions-item>
            </el-descriptions>
            <div class="step-box">
              <h4>研究步骤</h4>
              <el-steps :space="20" direction="vertical" finish-status="success">
                <el-step
                  v-for="(step, index) in (full_txt_art_analysis_data?.research_steps || [])"
                  :key="index"
                  :title="`step${index + 1}`"
                  :description="step"
                />
              </el-steps>
              <!-- 无步骤时的兜底 -->
              <div v-if="!full_txt_art_analysis_data?.research_steps || full_txt_art_analysis_data?.research_steps.length === 0" class="empty-tip">
                无相关研究步骤
              </div>
            </div>
          </el-collapse-item>

          <!-- 数据与工具 -->
          <el-collapse-item title="数据与工具" name="4">
            <el-row :gutter="20">
              <el-col :span="12">
                <div class="data-tool-box">
                  <h4>数据来源</h4>
                  <el-tag
                    class="data_src_tag"
                    :title="source"
                    v-for="(source, index) in (full_txt_art_analysis_data?.data_sources || [])"
                    :key="index"
                  >
                    {{ source }}
                  </el-tag>
                  <div v-if="!full_txt_art_analysis_data?.data_sources || full_txt_art_analysis_data?.data_sources.length === 0" class="empty-tip">
                    无相关数据来源
                  </div>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="data-tool-box">
                  <h4>分析工具</h4>
                  <el-tag
                    class="ans_tool_tag"
                    v-for="(tool, index) in (full_txt_art_analysis_data?.analysis_tools || [])"
                    :key="index"
                  >
                    {{ tool }}
                  </el-tag>
                  <div v-if="!full_txt_art_analysis_data?.analysis_tools || full_txt_art_analysis_data?.analysis_tools.length === 0" class="empty-tip">
                    无相关分析工具
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-collapse-item>

          <!-- 关键词与目录 -->
          <el-collapse-item title="关键词与目录" name="5">
            <el-row :gutter="20">
              <el-col :span="8">
                <div class="keyword-box">
                  <h4>关键词</h4>
                  <el-tag
                    v-for="(keyword, index) in (full_txt_art_analysis_data?.keywords || [])"
                    :key="index"
                    type="info"
                    effect="dark"
                  >
                    {{ keyword }}
                  </el-tag>
                  <div v-if="!full_txt_art_analysis_data?.keywords || full_txt_art_analysis_data?.keywords.length === 0" class="empty-tip">
                    无相关关键词
                  </div>
                </div>
              </el-col>
              <el-col :span="16">
                <div class="toc-box">
                  <h4>目录</h4>
                  <!-- 替换el-table为原生table -->
                  <table class="custom-table" v-if="full_txt_art_analysis_data?.table_of_contents && full_txt_art_analysis_data?.table_of_contents.length > 0">
                    <thead>
                      <tr>
                        <th>章节标题</th>
                        <th>页码</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, index) in full_txt_art_analysis_data?.table_of_contents" :key="index">
                        <td>{{ item.title }}</td>
                        <td>{{ item.page_no }}</td>
                      </tr>
                    </tbody>
                  </table>
                  <div v-if="!full_txt_art_analysis_data?.table_of_contents || full_txt_art_analysis_data?.table_of_contents.length === 0" class="empty-tip">
                    无相关目录信息
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-collapse-item>

          <!-- 创新点 -->
          <el-collapse-item title="研究创新点" name="6">
            <!-- 替换el-table为原生table -->
            <table class="custom-table innovation-table" v-if="full_txt_art_analysis_data?.innovations && full_txt_art_analysis_data?.innovations.length > 0">
              <thead>
                <tr>
                  <th>首次提出的内容</th>
                  <th>基于的基础</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in full_txt_art_analysis_data?.innovations" :key="index">
                  <td>{{ item['首次提出的概念/理论/技术/方法'] || '无' }}</td>
                  <td>
                    <el-tag
                      style="color: var(--tag-color-orange); background-color: var(--tag-bg-orange);"
                      v-for="(base, baseIndex) in (item['此概念/理论/技术/方法是在哪些概念/理论/技术/方法之上实现的'] || [])"
                      :key="baseIndex"
                    >
                      {{ base }}
                    </el-tag>
                    <span v-if="!item['此概念/理论/技术/方法是在哪些概念/理论/技术/方法之上实现的'] || item['此概念/理论/技术/方法是在哪些概念/理论/技术/方法之上实现的'].length === 0">无</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div v-if="!full_txt_art_analysis_data?.innovations || full_txt_art_analysis_data?.innovations.length === 0" class="empty-tip">
              无相关创新点信息
            </div>
          </el-collapse-item>

          <!-- 解决的研究问题：卡片列表样式 -->
          <el-collapse-item title="解决的研究问题" name="7">
            <div class="questions-card-list">
              <el-card 
                v-for="(item, index) in (full_txt_art_analysis_data?.solved_questions || [])" 
                :key="index" 
                class="question-card"
                shadow="hover"
              >
                <div slot="header" class="question-card-header">
                  <span class="question-index">问题 {{ index + 1 }}</span>
                  <span class="question-title">{{ item['解决的问题（疑问句）'] || '未命名问题' }}</span>
                </div>
                <div class="question-card-body">
                  <div class="question-item">
                    <label class="question-label">答案：</label>
                    <span class="question-content">{{ item.获得的答案 || '无相关答案' }}</span>
                  </div>
                  <div class="question-item">
                    <label class="question-label">使用方法：</label>
                    <span class="question-content">{{ item['使用的方法（详细描述100字左右）'] || '无相关方法描述' }}</span>
                  </div>
                </div>
              </el-card>
            </div>
            <div v-if="!full_txt_art_analysis_data?.solved_questions || full_txt_art_analysis_data?.solved_questions.length === 0" class="empty-tip">
              无相关解决的问题
            </div>
          </el-collapse-item>

          <!-- 局限性与未来方向 -->
          <el-collapse-item title="局限性与未来方向" name="8">
            <el-row :gutter="20">
              <el-col :span="12">
                <div class="limit-box">
                  <h4>研究局限性</h4>
                  <p>{{ full_txt_art_analysis_data?.limitations || '无相关局限性描述' }}</p>
                </div>
              </el-col>
              <el-col :span="12">
                <div class="future-box">
                  <h4>未来研究方向</h4>
                  <!-- 简洁版未来研究方向：仅保留轻量装饰 -->
                  <div class="future-simple-list" v-if="full_txt_art_analysis_data?.future_directions?.length > 0">
                    <div
                      v-for="(direction, index) in (full_txt_art_analysis_data?.future_directions || [])"
                      :key="index"
                      class="future-simple-item"
                    >
                      <!-- 小圆点作为序号装饰，替代复杂图标 -->
                      <span class="dot" :style="{backgroundColor: index % 3 === 0 ? 'var(--blue)' : index % 3 === 1 ? 'var(--green)' : 'var(--red)'}"></span>
                      <span class="future-simple-text">{{ direction }}</span>
                    </div>
                  </div>
                  <div v-else class="empty-tip">
                    无相关未来研究方向
                  </div>
                </div>
              </el-col>
            </el-row>
          </el-collapse-item>

          <!-- 其他信息 -->
          <el-collapse-item title="其他信息" name="9">
            <el-descriptions :column="1" border>
              <el-descriptions-item label="相似与差异">
                {{ full_txt_art_analysis_data?.similarities_differences || '无相关内容' }}
              </el-descriptions-item>
              <el-descriptions-item label="失败的概念">
                {{ full_txt_art_analysis_data?.failed_concepts || '无' }}
              </el-descriptions-item>
              <el-descriptions-item label="成本评估说明">
                {{ full_txt_art_analysis_data?.research_cost?.成本评估说明 ?? '未收录' }}
              </el-descriptions-item>
            </el-descriptions>
          </el-collapse-item>
        </el-collapse>
      </el-card>
    </div>
    
  </div>
</template>

<script>
import { cleanDoi } from "@/utils/doiUtils";

export default {
name: 'FullTxtArtAnalysisTip',
props: {
  full_txt_art_analysis_data: {
    type: [Object, null],
    required: false,
    default: () => ({})
  },
  // 接收父组件控制的显示状态
  visible: {
    type: Boolean,
    default: true
  }
},
data() {
  return {
    activeNames: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
    activeTab: 'goal'
  }
},
computed: {
  isDataLoaded() {
    if (!this.full_txt_art_analysis_data || typeof this.full_txt_art_analysis_data !== "object") {
      return false;
    }
    return Object.keys(this.full_txt_art_analysis_data).length > 0;
  }
},
methods: {
  cleanDoi(doi) {
    return cleanDoi(doi);
  },
  clossPaperAnalysis() {
    this.$emit('clossPaperAnalysis');
  },
  handleEscKeydown(e) {
    if (this.visible && (e.keyCode === 27 || e.key === 'Escape')) {
      this.clossPaperAnalysis();
    }
  },
  // 其他方法不变，新增这个方法
  getCostLevelClass(costLevel) {
    if (!costLevel) return '';
    // 自定义类名映射（完全可控）
    const costClassMap = {
      '超低成本': 'cost-level-ultra-low',
      '低成本': 'cost-level-low',
      '中等成本': 'cost-level-medium',
      '高成本': 'cost-level-high',
      '超高成本': 'cost-level-ultra-high'
    };
    // 模糊匹配（兼容带括号的文本，比如"超低成本(理论类)"）
    const levelKey = Object.keys(costClassMap).find(key => costLevel.includes(key));
    return levelKey ? costClassMap[levelKey] : '';
  },
},
mounted() {
  document.addEventListener('keydown', this.handleEscKeydown);
},
beforeDestroy() {
  document.removeEventListener('keydown', this.handleEscKeydown);
},
watch: {
  visible(newVal) {
    if (newVal) {
      document.addEventListener('keydown', this.handleEscKeydown);
    } else {
      document.removeEventListener('keydown', this.handleEscKeydown);
    }
  }
}
}
</script>

<style scoped>

/* 内容容器样式 - 使用全局背景和边框变量 */
.paper-analysis-container {
width: 90%;
/* background-color: var(--bg-back); */
min-height: auto;
border-radius: 20px;
/* box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2); */
/* padding: 20px; */
/* margin: 0 auto; */
/* border: 1px solid var(--label-border2); */
}

.box-card {
width: 100%;
/* background-color: var(--bg-back); */
color: var(--txt-color);
}

/* 内容文本框 - 使用全局文字颜色变量 */
.content-box {
padding: 10px;
line-height: 1.8;
font-size: var(--font-size);
color: var(--txt-color);
}

.step-box {
margin-top: 20px;
}

/* 标题文字 - 使用全局文字颜色变量 */
.step-box h4, .data-tool-box h4, .keyword-box h4, .toc-box h4, .limit-box h4, .future-box h4 {
margin: 0 0 10px 0;
font-size: 15px;
color: var(--txt-color);
}

.toc-box {
height: 400px;
overflow-y: auto;
}

.limit-box, .future-box {
padding: 10px;
}

.el-steps {
margin-left: 20px;
}

.el-tag {
margin: 0 5px 5px 0;
}

/* 加载状态样式 */
.loading-box {
min-height: 200px;
}

/* 空数据提示样式 - 使用全局次要文字颜色 */
.empty-tip {
margin-top: 10px;
color: var(--txt-color3);
font-size: var(--font-size);
text-align: center;
}

/* 手风琴标题增强样式 - 使用全局变量 */
.custom-collapse {
--el-collapse-header-text-color: var(--txt-color);
--el-collapse-header-font-size: 16px;
--el-collapse-header-height: 60px;
}

/* Vue2 样式穿透：使用 /deep/ 替代 ::v-deep */
.custom-collapse /deep/ .el-collapse-item__header {
font-weight: 600;
padding-left: 20px;
border-left: 4px solid var(--blue);
background-color: var(--bg-back);
transition: all 0.3s ease;
color: var(--txt-color) !important;
}

/* 鼠标悬浮时的标题效果 - 使用全局主题色 */
.custom-collapse /deep/ .el-collapse-item__header:hover {
background-color: var(--label-bg-hover);
color: var(--blue) !important;
}

/* 调整标题图标的位置 */
.custom-collapse /deep/ .el-collapse-item__header .el-collapse-item__arrow {
right: 20px;
color: var(--txt-color2) !important;
}

/* 解决的研究问题 - 卡片列表样式 */
.questions-card-list {
display: flex;
flex-direction: column;
gap: 16px;
margin-top: 8px;
}

.question-card {
border-radius: 6px;
border: 1px solid var(--label-border);
background-color: var(--label-bg) !important;
}

.question-card-header {
display: flex;
align-items: center;
gap: 8px;
padding: 0;
}

/* 问题编号角标 - 使用全局主题色 */
.question-index {
background-color: var(--blue);
color: var(--light);
padding: 2px 8px;
border-radius: 4px;
font-size: 12px;
font-weight: 600;
}

/* 问题标题 - 使用全局文字颜色 */
.question-title {
font-size: var(--font-size);
font-weight: 500;
color: var(--txt-color);
flex: 1;
line-height: 1.5;
}

.question-card-body {
padding: 12px 0 0 0;
}

.question-item {
display: flex;
flex-direction: column;
margin-bottom: 8px;
line-height: 1.8;
}

/* 问题标签 - 使用全局次要文字颜色 */
.question-label {
font-weight: 600;
color: var(--txt-color2);
font-size: 13px;
margin-bottom: 4px;
}

/* 问题内容 - 使用全局主要文字颜色 */
.question-content {
font-size: var(--font-size);
color: var(--txt-color);
word-break: break-all;
}

/* 简洁版未来研究方向样式 - 核心修改 */
.future-simple-list {
display: flex;
flex-direction: column;
gap: 10px; /* 紧凑的间距，保持简洁 */
}

.future-simple-item {
display: flex;
align-items: flex-start;
line-height: 1.8; /* 舒适的行高，提升可读性 */
font-size: var(--font-size);
color: var(--txt-color);
}

/* 彩色小圆点装饰：使用全局主题色变量 */
.dot {
display: inline-block;
width: 6px;
height: 6px;
border-radius: 50%;
margin-right: 8px;
margin-top: 7px; /* 垂直居中对齐 */
flex-shrink: 0; /* 防止小圆点被压缩 */
}

.future-simple-text {
flex: 1;
word-break: break-all; /* 长文本换行 */
color: var(--txt-color);
}

/* 原生table样式 - 模拟el-table的border样式 */
.custom-table {
width: 100%;
border-collapse: collapse;
font-size: var(--font-size);
color: var(--txt-color);
margin: 0;
}

.custom-table th,
.custom-table td {
border: 1px solid var(--label-border);
padding: 12px 8px;
text-align: left;
box-sizing: border-box;
}

.custom-table th {
background-color: var(--bg-back);
font-weight: 600;
color: var(--txt-color2);
}

.custom-table tbody tr:hover {
background-color: var(--label-bg-hover);
}

/* 创新点表格额外样式 */
.innovation-table {
margin-top: 8px;
}

.innovation-table td {
vertical-align: top;
}
.data_src_tag {
color: var(--tag-color-blue);
background-color: var(--tag-bg-blue);
max-width: 450px; /* 最大宽度450px */
white-space: nowrap; /* 强制文本单行显示 */
overflow: hidden; /* 隐藏超出部分 */
text-overflow: ellipsis; /* 超出部分显示省略号 */
}
.ans_tool_tag {
color: var(--tag-color-green); 
background-color: var(--tag-bg-green);
max-width: 450px; /* 最大宽度450px */
white-space: nowrap; /* 强制文本单行显示 */
overflow: hidden; /* 隐藏超出部分 */
text-overflow: ellipsis; /* 超出部分显示省略号 */
}

/* 成本级别自定义标签样式（完全独立，不依赖 Element） */
.cost-level-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  white-space: nowrap;
  /* 对齐 Element 标签的默认样式，保持视觉统一 */
  box-sizing: border-box;
}

/* 超低成本 */
.cost-level-ultra-low {
  background-color: var(--tag-bg-green) !important;
  color: var(--tag-color-green) !important;
  border: 1px solid var(--tag-bg-green);
}

/* 低成本 */
.cost-level-low {
  background-color: var(--tag-bg-blue) !important;
  color: var(--tag-color-blue) !important;
  border: 1px solid var(--tag-bg-blue);
}

/* 中等成本 */
.cost-level-medium {
  background-color: var(--tag-bg-orange) !important;
  color: var(--tag-color-orange) !important;
  border: 1px solid var(--tag-bg-orange);
}

/* 高成本 */
.cost-level-high {
  background-color: var(--tag-bg-red) !important;
  color: var(--tag-color-red) !important;
  border: 1px solid var(--tag-bg-red);
}

/* 超高成本 */
.cost-level-ultra-high {
  background-color: var(--tag-bg-gray) !important; /* 修正之前的gary拼写 */
  color: var(--tag-color-gray) !important;
  border: 1px solid var(--tag-bg-gray);
}


</style>
